// Resolves a vehicle's body-type silhouette (reused from the
// vehicle-inspections storage bucket, same assets the daily inspection
// camera overlay uses) and bakes damage markers onto it as a flat image --
// used by both the Claims tab review pane and generateClaimPdf.ts so the
// composed diagram looks identical in both places.
import { supabase } from "@/lib/supabase";
import { loadVehicleTypes } from "@/lib/vehicleTypes";
import { DamageDiagramView, IncidentDamageMarker } from "@/lib/incidentReports";

export const DAMAGE_DIAGRAM_VIEWS: { view: DamageDiagramView; label: string }[] = [
  { view: "front", label: "Front" },
  { view: "rear", label: "Rear" },
  { view: "driver", label: "Driver Side" },
  { view: "passenger", label: "Passenger Side" },
];

const SEVERITY_COLORS: Record<string, string> = {
  Minor: "#22c55e",
  Moderate: "#f59e0b",
  Severe: "#ef4444",
};

/** Same path convention as VehicleInspection.tsx's getOverlayStoragePath. */
function overlayStoragePath(overlayAssetKey: string, view: DamageDiagramView): string {
  return `${overlayAssetKey}/${overlayAssetKey}_${view}.png`;
}

export async function resolveOverlayAssetKey(vehicleType: string | null | undefined): Promise<string | null> {
  if (!vehicleType) return null;
  const types = await loadVehicleTypes();
  return types.find((t) => t.vehicle_type === vehicleType)?.overlay_asset_key || null;
}

export function overlayPublicUrl(overlayAssetKey: string, view: DamageDiagramView): string {
  return supabase.storage.from("vehicle-inspections").getPublicUrl(overlayStoragePath(overlayAssetKey, view)).data.publicUrl;
}

function loadImageElement(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Draws the silhouette + numbered severity-coloured pins onto an offscreen
 * canvas and returns it as a PNG data URL, for embedding in the PDF (jsPDF
 * can only take a static image, not a live DOM overlay). */
export async function renderDiagramWithMarkers(
  imageUrl: string,
  markers: IncidentDamageMarker[]
): Promise<{ dataUrl: string; width: number; height: number } | null> {
  const img = await loadImageElement(imageUrl);
  if (!img || !img.naturalWidth) return null;

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // The overlay PNGs have a transparent background, not white -- fill white
  // first so they don't pick up whatever's behind them (the app's dark
  // theme here, otherwise a jsPDF page).
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const r = Math.max(14, canvas.width * 0.025);
  markers.forEach((m, i) => {
    const x = (m.x_percent / 100) * canvas.width;
    const y = (m.y_percent / 100) * canvas.height;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = SEVERITY_COLORS[m.severity] || SEVERITY_COLORS.Minor;
    ctx.fill();
    ctx.lineWidth = Math.max(2, r * 0.15);
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${r}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), x, y);
  });

  return { dataUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
}
